import { closeModal } from 'actions/modal';
import Button from 'components/atoms/Button';
import Typography from 'components/atoms/Typography';
import { useState } from 'react';
import { useThunkDispatch } from 'utils/reduxHook';
import {
  FullWidthSection,
  SplitWidthSection,
  SectionContentType,
  SectionPrefix,
  StartPageState,
  HubAuthoringSelectSectionModalPages,
} from 'pages/_AdminAuthoringPages/AdminHubAuthoring/types';
import { HubSectionType, HubSplitSectionContentType } from 'utils/gloo-types';
import ModalWrapper from '../ModalWrapper';
import BackButton from './BackButton';
import SplitSectionOptions from './SplitSectionOptions';
import FullWidthSectionOptions from './FullSectionOptions';
import StartPageOptions from './StartPageOptions';

function AddHubSection({ onConfirm }: { onConfirm: (n: SectionContentType) => void }) {
  const [startPageState, setStartPageState] = useState<StartPageState>(SectionPrefix.FullWidth);
  const [fullWidthSectionPageState, setFullWidthSectionPageState] = useState<FullWidthSection>({
    type: HubSectionType.IMAGE,
  });
  const [splitWidthSectionPageState, setSplitWidthSectionPageState] = useState<SplitWidthSection>({
    type: HubSectionType.SPLIT,
    a: HubSplitSectionContentType.TEXT,
    b: HubSplitSectionContentType.IMAGE,
  });

  const [page, setPage] = useState<HubAuthoringSelectSectionModalPages>(HubAuthoringSelectSectionModalPages.StartPage);
  const globalDispatch = useThunkDispatch();

  /**
   * General methods
   */
  const onBackButtonClick = () => {
    setPage(HubAuthoringSelectSectionModalPages.StartPage);
  };

  // When the continue button is clicked navigate to the appropriate page,
  // or close the modal after executing the onConfirm callback.
  const onContinueButtonClick = () => {
    switch (page) {
      case HubAuthoringSelectSectionModalPages.StartPage: {
        switch (startPageState) {
          case HubSectionType.RESOURCE: {
            onConfirm({ type: HubSectionType.RESOURCE });
            globalDispatch(closeModal());
            break;
          }
          case SectionPrefix.FullWidth: {
            setPage(HubAuthoringSelectSectionModalPages.FullWidthPage);
            break;
          }
          case SectionPrefix.FiftyFifty: {
            setPage(HubAuthoringSelectSectionModalPages.SplitWidthPage);
            break;
          }
          default: {
            console.error(`No case implemented for ${startPageState}`);
          }
        }
        break;
      }
      case HubAuthoringSelectSectionModalPages.FullWidthPage: {
        onConfirm(fullWidthSectionPageState);
        globalDispatch(closeModal());
        break;
      }
      case HubAuthoringSelectSectionModalPages.SplitWidthPage: {
        onConfirm(splitWidthSectionPageState);
        globalDispatch(closeModal());
        break;
      }
      default: {
        console.error(`No case implemented for ${page}`);
      }
    }
  };

  /**
   * Rendering methods
   */
  const renderTitle = () => {
    if (page === HubAuthoringSelectSectionModalPages.StartPage) return 'What kind of section do you want to add?';
    return 'What kind of content do you want?';
  };

  const renderPageOptions = () => {
    switch (page) {
      case HubAuthoringSelectSectionModalPages.StartPage: {
        return <StartPageOptions startPageState={startPageState} setStartPageState={setStartPageState} />;
      }
      case HubAuthoringSelectSectionModalPages.FullWidthPage: {
        return (
          <FullWidthSectionOptions
            fullWidthSectionState={fullWidthSectionPageState}
            setFullWidthSectionState={setFullWidthSectionPageState}
          />
        );
      }
      case HubAuthoringSelectSectionModalPages.SplitWidthPage: {
        return (
          <SplitSectionOptions
            splitWidthSectionState={splitWidthSectionPageState}
            setSplitWidthSectionState={setSplitWidthSectionPageState}
          />
        );
      }
      default: {
        console.error(`${page} has not been implemented`);
      }
    }
  };

  return (
    <ModalWrapper type="full-width">
      <BackButton onClick={onBackButtonClick} isVisible={page !== HubAuthoringSelectSectionModalPages.StartPage} />
      <div className="flex flex-col m-auto py-[75px]">
        <Typography variant="h4" className="flex text-center text-glooCharcoal mb-5 max-w-[240px] m-auto">
          {renderTitle()}
        </Typography>
        <div>{renderPageOptions()}</div>
      </div>
      <div className="flex flex-col m-auto max-w-[240px]">
        <Button
          className="justify-center"
          type="primary"
          onClick={onContinueButtonClick}
          dataDurableId=""
          text="Continue"
        />
      </div>
    </ModalWrapper>
  );
}

export default AddHubSection;
